import { Button } from "@heroui/react";
import {
	type AdminGoldenAsset,
	type AdminLocation,
	type CreateLocationInput,
	type GenerationTargetType,
	GoldenAssetKind,
	GoldenAssetOwnerType,
	GenerationTargetType as TargetType,
	type UpdateLocationInput,
} from "@storyline/shared";
import { useState } from "react";
import AssetRow from "@/src/series/components/asset-row";
import LocationForm from "@/src/series/components/location-form";

const LOCATION_KINDS = [GoldenAssetKind.LOCATION_MASTER, GoldenAssetKind.LOCATION_ANGLE];

type LocationCardProps = {
	location: AdminLocation;
	assets: AdminGoldenAsset[];
	generatingKeys: Set<string>;
	onEdit: (locationId: string, body: UpdateLocationInput) => Promise<void>;
	onRemove: (locationId: string) => Promise<void>;
	onGenerate: (targetType: GenerationTargetType, targetId: string, kind: GoldenAssetKind) => Promise<void>;
	onApprove: (assetId: string) => Promise<void>;
	onLock: (assetId: string) => Promise<void>;
	onUnlock: (assetId: string) => Promise<void>;
};

const LocationCard: React.FC<LocationCardProps> = ({
	location,
	assets,
	generatingKeys,
	onEdit,
	onRemove,
	onGenerate,
	onApprove,
	onLock,
	onUnlock,
}) => {
	const [editing, setEditing] = useState(false);

	const initial: CreateLocationInput = {
		name: location.name,
		description: location.description,
		lighting: location.lighting,
		palette: location.palette,
		cameraNotes: location.cameraNotes,
		ambienceNotes: location.ambienceNotes,
	};

	return (
		<div className="flex flex-col gap-3 rounded-xl border border-border-soft bg-surface-2 p-4">
			<div className="flex items-start justify-between gap-3">
				<div className="flex flex-col gap-1">
					<span className="font-semibold text-foreground">{location.name}</span>
					<span className="text-muted text-sm">{location.description}</span>
				</div>
				<div className="flex shrink-0 gap-1">
					<Button size="sm" variant="ghost" onPress={() => setEditing((previous) => !previous)}>
						{editing ? "Fermer" : "Éditer"}
					</Button>
					<Button size="sm" variant="ghost" className="text-danger" onPress={() => void onRemove(location.id)}>
						Supprimer
					</Button>
				</div>
			</div>
			{editing ? (
				<LocationForm
					initial={initial}
					submitLabel="Enregistrer"
					onSubmit={async (body) => {
						await onEdit(location.id, body);
						setEditing(false);
					}}
					onCancel={() => setEditing(false)}
				/>
			) : null}
			<AssetRow
				ownerType={GoldenAssetOwnerType.LOCATION}
				ownerId={location.id}
				targetType={TargetType.LOCATION}
				targetId={location.id}
				kinds={LOCATION_KINDS}
				assets={assets}
				generatingKeys={generatingKeys}
				onGenerate={onGenerate}
				onApprove={onApprove}
				onLock={onLock}
				onUnlock={onUnlock}
			/>
		</div>
	);
};

export default LocationCard;
